// src/components/MarketInsights/components/UnitsListDrawer.tsx

import React from "react";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Divider,
  Skeleton,
  useTheme,
  alpha,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { UnitDetail } from "../types";
import {
  formatCurrency,
  formatBedBath,
  formatSqft,
  formatDate,
} from "../utils/formatters";

interface UnitsListDrawerProps {
  open: boolean;
  onClose: () => void;
  units: UnitDetail[] | undefined;
  isLoading: boolean;
  selectedDate: string | null;
  onUnitClick: (unit: UnitDetail) => void;
}

const UnitsListDrawer: React.FC<UnitsListDrawerProps> = ({
  open,
  onClose,
  units,
  isLoading,
  selectedDate,
  onUnitClick,
}) => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = React.useState("");

  const filteredUnits = React.useMemo(() => {
    if (!units) return [];
    if (!searchTerm) return units;

    const term = searchTerm.toLowerCase();
    return units.filter(
      (unit) =>
        unit.property_name?.toLowerCase().includes(term) ||
        unit.unit_number?.toLowerCase().includes(term) ||
        unit.model_name?.toLowerCase().includes(term) ||
        unit.property_neighborhood?.toLowerCase().includes(term)
    );
  }, [units, searchTerm]);

  // Group units by property
  const groupedUnits = React.useMemo(() => {
    const groups: { [key: string]: UnitDetail[] } = {};
    filteredUnits.forEach((unit) => {
      if (!groups[unit.property_id]) {
        groups[unit.property_id] = [];
      }
      groups[unit.property_id].push(unit);
    });
    return groups;
  }, [filteredUnits]);

  const dateLabel = selectedDate
    ? selectedDate === "now"
      ? "Available Now"
      : `Available ${formatDate(selectedDate)}`
    : "";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", sm: 420 } },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={600}>
                {dateLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {filteredUnits.length} units
                {Object.keys(groupedUnits).length > 0 &&
                  ` across ${Object.keys(groupedUnits).length} properties`}
              </Typography>
            </Box>
            <IconButton aria-label="Close units" title="Close units" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            placeholder="Search units..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          {isLoading ? (
            <Box sx={{ p: 2 }}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Box key={i} sx={{ mb: 2 }}>
                  <Skeleton width="40%" height={24} />
                  <Skeleton width="100%" height={60} sx={{ mt: 1 }} />
                  <Skeleton width="100%" height={60} sx={{ mt: 1 }} />
                </Box>
              ))}
            </Box>
          ) : filteredUnits.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                p: 4,
                color: "text.secondary",
              }}
            >
              <HomeIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
              <Typography>No units found</Typography>
              {searchTerm && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Try adjusting your search
                </Typography>
              )}
            </Box>
          ) : (
            <List disablePadding>
              {Object.entries(groupedUnits).map(
                ([propertyId, propertyUnits], groupIndex) => (
                  <React.Fragment key={propertyId}>
                    {/* Property Header - Normal scroll, not sticky */}
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        backgroundColor: alpha(theme.palette.grey[500], 0.08),
                        borderBottom: `1px solid ${theme.palette.divider}`,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        {propertyUnits[0].property_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {propertyUnits[0].property_neighborhood ||
                          propertyUnits[0].property_address}{" "}
                        • {propertyUnits.length} unit
                        {propertyUnits.length > 1 ? "s" : ""}
                      </Typography>
                    </Box>

                    {/* Units for this property */}
                    {propertyUnits.map((unit, unitIndex) => (
                      <ListItemButton
                        key={unit.id}
                        onClick={() => onUnitClick(unit)}
                        sx={{
                          borderBottom:
                            unitIndex < propertyUnits.length - 1
                              ? `1px solid ${alpha(theme.palette.divider, 0.5)}`
                              : "none",
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.1
                              ),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                              fontSize: "0.875rem",
                            }}
                          >
                            {unit.bedrooms === 0 ? "S" : `${unit.bedrooms}B`}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                Unit {unit.unit_number}
                              </Typography>
                              <Chip
                                label={formatCurrency(unit.current_price)}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatBedBath(unit.bedrooms, unit.bathrooms)} •{" "}
                              {formatSqft(unit.square_feet)}
                              {unit.model_name && ` • ${unit.model_name}`}
                            </Typography>
                          }
                        />
                        <ChevronRightIcon color="action" />
                      </ListItemButton>
                    ))}
                  </React.Fragment>
                )
              )}
            </List>
          )}
        </Box>

        {/* Summary Footer */}
        {filteredUnits.length > 0 && (
          <Box
            sx={{
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              backgroundColor: alpha(theme.palette.grey[500], 0.04),
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Price Range
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(
                    Math.min(...filteredUnits.map((u) => u.current_price || 0))
                  )}{" "}
                  -{" "}
                  {formatCurrency(
                    Math.max(...filteredUnits.map((u) => u.current_price || 0))
                  )}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary">
                  Average
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(
                    filteredUnits.reduce(
                      (sum, u) => sum + (u.current_price || 0),
                      0
                    ) / filteredUnits.length
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default UnitsListDrawer;
